import React, { useMemo, useCallback } from "react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FormInput } from "../../components/FormInput";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import NoInfo from "../../components/NoInfo";
import { getSubItemsByItemNo } from "../../utility/service/api-service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addDate,
  checkDpSuccess,
  getFormErrorMessage,
  setformate,
} from "../../utility/service/tool-service";
import { getAnalyzeInitData } from "../../utility/service/statistics-service";
import { queryCompanyTagList } from "../../utility/service/company-service";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import ReactECharts from "echarts-for-react";

const ActivityAnalyzeIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setItemObj, setAction } = useOutletContext();
  const [currentDate, setCurrentDate] = useState("");
  const [type, setType] = useState("1");
  const [expandedRows, setExpandedRows] = useState(null);
  const { currentStatus, setCurrentStatus } = useOutletContext();
  const [effectiveType, setEffectiveType] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [reqQueryHeader, setReqQueryHeader] = useState({});
  const [, loading] = useContext(SystemLoading);
  const [columns, setColumns] = useState([]);
  const [showLegend, setShowLegend] = useState(true);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'pie'

  // 共用顏色配置 - 21種特色系（由深到淺）
  const chartColors = [
    "#1f77b4", // 深藍色
    "#ff7f0e", // 橙色
    "#2ca02c", // 綠色
    "#d62728", // 紅色
    "#9467bd", // 紫色
    "#8c564b", // 棕色
    "#e377c2", // 粉紅色
    "#7f7f7f", // 灰色
    "#bcbd22", // 橄欖綠
    "#17becf", // 青藍色
    "#aec7e8", // 淺藍色
    "#ffbb78", // 淺橙色
    "#98df8a", // 淺綠色
    "#ff9896", // 淺紅色
    "#c5b0d5", // 淺紫色
    "#c49c94", // 淺棕色
    "#f7b6d3", // 淺粉色
    "#c7c7c7", // 淺灰色
    "#dbdb8d", // 淺橄欖綠
    "#9edae5", // 淺青藍色
    "#ffd700"  // 金色
  ];

  const {
    register,
    handleSubmit,
    clearErrors,
    getValues,
    setValue,
    resetField,
    control,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      type: "",
      startDate: "", //起
      endDate: "",
      keyword: "",
    },
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    const handleResize = () => {
      // 小螢幕自動隱藏，大螢幕自動顯示
      if (window.innerWidth < 1200) {
        setShowLegend(false);
      } else {
        setShowLegend(true);
      }
    };

    // 先執行一次，確保初始化狀態正確
    handleResize();

    // 監聽 resize
    window.addEventListener("resize", handleResize);

    // 清除監聽，避免記憶體洩漏
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const axios_getAnalyzeInitData = useMemo(
    () => (reqHeader, readMore = false) => {
      checkLoading(loading);
      getAnalyzeInitData(reqHeader)
        .then((res) => {
          if (checkDpSuccess(res)) {
            if (readMore === true) {
              setColumns((pre) => {
                return [...pre, ...res.data.RespBody.data];
              });
            } else {
              setColumns(res.data.RespBody.data);
            }
          } else if (res?.data?.ResHeader?.rtnCode === "1298") {
            if (readMore === false) {
              setColumns([]);
            }
          }
        })
        .finally(() => checkLoading(loading, false));
    },
    [loading]
  );

  const setReqBody = useCallback((type = "1", sDate = "", eDate = "", keyword = null, effectiveType = null, tag = null) => {
    let reqBody = {};
    reqBody = {
      startDate: setformate(sDate !== "" ? sDate : getValues("startDate"), "YYYY/MM/DD"),
      endDate: setformate(eDate !== "" ? eDate : getValues("endDate"), "YYYY/MM/DD"),
      keyword: keyword,
      type: type,
      effectiveType: effectiveType,
      tag: tag,
    };
    return reqBody;
  }, [getValues]);

  const initDate = useMemo(() => () => {
    let date = new Date();
    let SDate = setformate(addDate(date, -6), "YYYY-MM-DD");
    let EDate = setformate(date, "YYYY-MM-DD");
    setValue("startDate", new Date(SDate), { shouldValidate: true });
    setValue("endDate", new Date(EDate), { shouldValidate: true });
    setCurrentDate(setformate(date, "YYYY-MM-DD"));
    setType("1");
    setReqQueryHeader(setReqBody("1", SDate, EDate));
  }, [setValue, setReqBody]);

  useEffect(() => {
    initDate();
    getSubItemsByItemNo("EFFECTIVE_TYPE", 17).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setEffectiveType(newArray);
      }
    });

    // 載入 tag 列表
    queryCompanyTagList().then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.tagList || [];
        let formattedTagList = data.map((item, index) => {
          return {
            ...item,
            label: item.tagName,
            value: item.companiesTagSeq,
            index: index,
          };
        });
        setTagList(formattedTagList);
      }
    });
  }, [initDate]);

  useEffect(() => {
    if (JSON.stringify(currentStatus) === "{}" && JSON.stringify(reqQueryHeader) !== "{}") {
      //抓取新資料
      axios_getAnalyzeInitData(reqQueryHeader);
    }
  }, [reqQueryHeader, axios_getAnalyzeInitData, currentStatus]);

  const preMonth = () => {
    let sDate = new Date(currentDate);
    sDate.setMonth(sDate.getMonth() - 1);
    sDate.setDate(1);
    setValue("startDate", new Date(sDate), { shouldValidate: true });
    let eDate = new Date(getValues("startDate"));
    eDate.setMonth(sDate.getMonth() + 1);
    eDate.setDate(eDate.getDate() - 1);
    setValue("endDate", new Date(eDate), { shouldValidate: true });
    clearErrors(["startDate", "endDate"]);
    setCurrentDate((pre) => {
      let date = new Date(pre);
      date = setformate(date.setMonth(date.getMonth() - 1), "YYYY-MM-DD");
      return date;
    });
    setReqQueryHeader(setReqBody(type));
  };

  const nextMonth = () => {
    let sDate = new Date(currentDate);
    sDate.setMonth(sDate.getMonth() + 1);
    sDate.setDate(1);
    setValue("startDate", new Date(sDate), { shouldValidate: true });
    let eDate = new Date(getValues("startDate"));
    eDate.setMonth(sDate.getMonth() + 1);
    eDate.setDate(eDate.getDate() - 1);
    setValue("endDate", new Date(eDate), { shouldValidate: true });
    clearErrors(["startDate", "endDate"]);
    setCurrentDate((pre) => {
      let date = new Date(pre);
      date = setformate(date.setMonth(date.getMonth() + 1), "YYYY-MM-DD");
      return date;
      // return pre.setMonth(pre.getMonth() + 1);
    });
    setReqQueryHeader(setReqBody(type));
  };

  const onSubmit = (data) => {
    setCurrentStatus({});
    // 查詢前先清空現有資料
    setColumns([]);
    const keyword = type === "2" ? data.keywordSearch || null : null;
    const effectiveType = type === "2" ? data.effectiveType || null : null;
    const tag = type === "2" ? selectedTag : null;
    setReqQueryHeader(setReqBody(type, data.startDate, data.endDate, keyword, effectiveType, tag));
  };

  const editItem = (item, act) => {
    setItemObj(item);
    navigate(act);
    setAction(act);
  };

  // 一鍵展開/收合所有行
  const toggleAllExpanded = () => {
    if (isAllExpanded) {
      // 收合所有行
      setExpandedRows({});
      setIsAllExpanded(false);
    } else {
      // 展開所有行
      const allExpandedRows = {};
      columns.forEach((row) => {
        if (type === "1") {
          allExpandedRows[row.event] = true;
        } else if (type === "2") {
          allExpandedRows[row.companySeq] = true;
        }
      });
      setExpandedRows(allExpandedRows);
      setIsAllExpanded(true);
    }
  };

  const checkKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const changeType = (value) => {
    setType(value);
    resetField("effectiveType");
    setSelectedTag(null);
    // 清空資料顯示
    setColumns([]);
    // 切換 type 時，預設變回表格模式
    setViewMode('table');
    // 立即觸發查詢
    setReqQueryHeader(setReqBody(value));
    // 重置展開狀態
    setExpandedRows({});
    setIsAllExpanded(false);
  };

  //多國語系 月份對照
  const monthMapping = [
    {
      month: "Jan",
      value: "01",
    },
    {
      month: "Feb",
      value: "02",
    },
    {
      month: "Mar",
      value: "03",
    },
    {
      month: "Apr",
      value: "04",
    },
    {
      month: "May",
      value: "05",
    },
    {
      month: "Jun",
      value: "06",
    },
    {
      month: "Jul",
      value: "07",
    },
    {
      month: "Aug",
      value: "08",
    },
    {
      month: "Sep",
      value: "09",
    },
    {
      month: "Oct",
      value: "10",
    },
    {
      month: "Nov",
      value: "11",
    },
    {
      month: "Dec",
      value: "12",
    },
  ];
  const mappingMath = (month) => {
    let filterMon = monthMapping?.filter((item) => item.value === month);
    return t(filterMon?.[0]?.month);
  };

  // 展開子表格：渲染 userList
  const rowExpansionTemplateByAction = (event) => {
    return (
      <div className="ml-8">
        <DataTable
          value={event.userList}
          tableStyle={{ minWidth: "55rem", tableLayout: "fixed", width: "100%" }}
          emptyMessage={<NoInfo panel="table" />}
        >
          <Column field="companyName" header={t("companyName")} style={{ width: "40%" }} />
          <Column field="companyCountryName" header={t("country")} style={{ width: "25%" }} />
          <Column field="companyTypeName" header={t("type")} style={{ width: "15%" }} body={companyTypeNameTemplate} />
          <Column field="count" header={t("times")} style={{ width: "10%" }} />
          <Column
            field="percentage"
            header={t("percentage")}
            style={{ width: "10%" }}
            body={(rowData) => `${rowData.percentage}`}
          />
        </DataTable>
      </div>
    );
  };

  // 展開子表格：渲染 userList
  const rowExpansionTemplateByCompany = (event) => {
    return (
      <div className="ml-8">
        <DataTable
          value={event.actionList}
          tableStyle={{ minWidth: "55rem", tableLayout: "fixed", width: "100%" }}
          emptyMessage={<NoInfo panel="table" />}
        >
          width
          <Column field="eventName" header={t("eventName")} style={{ width: "33%" }} />
          <Column field="count" header={t("times")} style={{ width: "33%" }} />
          <Column
            field="percentage"
            header={t("percentage")}
            style={{ width: "33%" }}
            body={(rowData) => `${rowData.percentage}`}
          />
        </DataTable>
      </div>
    );
  };

  const companyLevelNameTemplate = (rowData, props) => {
    if (rowData) {
      switch (rowData?.effectiveType) {
        case "0": //正式
          return (
            <div className="user-avatar-button">
              <span style={{ color: "var(--primary-color)", fontSize: "1rem" }} className="contract-str">
                {t("formal")}
              </span>
            </div>
          );
        case "1": //試用
          return (
            <div className="user-avatar-button">
              <span style={{ color: "var(--gray-400)", fontSize: "1rem" }} className="contract-str">
                {t("trial")}
              </span>
            </div>
          );
        default:
          return null;
      }
    }
  };

  const companyTypeNameTemplate = (rowData, props) => {
    if (rowData) {
      switch (rowData?.companyType) {
        case "A": //廠商
          return (
            <div className="user-avatar-button">
              <span style={{ color: "var(--green-500)", fontSize: "1rem" }} className="contract-str">
                {rowData?.companyTypeName || t("company.a")}
              </span>
            </div>
          );
        case "B": //顧問
          return (
            <div className="user-avatar-button">
              <span style={{ color: "var(--blue-500)", fontSize: "1rem" }} className="contract-str">
                {rowData?.companyTypeName || t("company.b")}
              </span>
            </div>
          );
        case "M": //管理者
          return (
            <div className="user-avatar-button">
              <span style={{ color: "var(--mint-500)", fontSize: "1rem" }} className="contract-str">
                {rowData?.companyTypeName || t("company.m")}
              </span>
            </div>
          );
        default:
          return rowData?.companyTypeName ? (
            <div className="user-avatar-button">
              <span style={{ color: "var(--gray-400)", fontSize: "1rem" }} className="contract-str">
                {rowData.companyTypeName}
              </span>
            </div>
          ) : null;
      }
    }
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <>
          <Button
            tooltip={t("button.detail")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              editItem(rowData, "detail");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-eye" />
          </Button>
        </>
      );
    }
  };

  // 共用函數：處理數據並添加"其他"項目
  const processChartData = (data) => {
    // 先排序，取前15個
    const sortedData = [...data].sort(
      (a, b) => Number(b.count) - Number(a.count) // 轉成數字比較
    );

    const topFifteen = sortedData.slice(0, 15);
    const others = sortedData.slice(15);

    // 計算其他的總和
    const othersTotal = others.reduce((sum, item) => sum + Number(item.count), 0); // 轉成數字加總

    // 最終資料陣列
    const finalData = [...topFifteen].map((item) => ({
      ...item,
      count: Number(item.count), // 確保 count 為數字
    }));

    if (othersTotal > 0) {
      const total = finalData.reduce((sum, item) => sum + item.count, 0) + othersTotal;
      const othersPercentage = ((othersTotal / total) * 100).toFixed(1) + '%';
      finalData.push({ 
        eventName: t("other"), 
        companyName: t("other"), 
        count: othersTotal,
        percentage: othersPercentage
      });
    }

    return finalData;
  };

  const getPieOptions = (data) => {
    const finalData = processChartData(data);

    return {
      color: chartColors, // 使用共用顏色配置
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: showLegend
        ? {
            orient: "horizontal",
            bottom: "5%",
            left: "center",
            itemGap: 8,
            itemWidth: 12,
            itemHeight: 12,
          }
        : { show: false }, // 隱藏圖例
      series: [
        {
          type: "pie",
          radius: ["30%", "65%"], // 增大圓餅圖，更充分利用空間
          center: ["50%", "45%"], // 稍微上移，為底部圖例留空間
          data: finalData.map((item, index) => ({
            value: item.count,
            name: type === "1" ? item.eventName : item.companyName,
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  const getBarOptions = (data) => {
    const finalData = processChartData(data);

    // 準備圖表資料（反轉順序，讓大的在上面）
    const categories = finalData.map(item => type === "1" ? item.eventName : item.companyName).reverse();
    const values = finalData.map(item => item.count).reverse();

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow"
        },
        textStyle: {
          fontSize: 16
        },
        formatter: function(params) {
          const item = params[0];
          const total = values.reduce((sum, val) => sum + val, 0);
          const percentage = ((item.value / total) * 100).toFixed(1);
          return `${item.name}: ${item.value} (${percentage}%)`;
        }
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        top: "3%",
        containLabel: true
      },
      xAxis: {
        type: "value",
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: "#666",
          fontSize: 16
        }
      },
      yAxis: {
        type: "category",
        data: categories,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: "#333",
          fontSize: 16,
          interval: 0,
          formatter: function(value) {
            // 如果標籤太長，進行截斷
            if (value.length > 12) {
              return value.substring(0, 12) + "...";
            }
            return value;
          }
        }
      },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: {
            color: function(params) {
              // 因為長條圖數據被反轉了，所以顏色索引也要反轉來匹配圓餅圖
              const reversedIndex = (values.length - 1) - params.dataIndex;
              return chartColors[reversedIndex % chartColors.length];
            },
            borderRadius: [0, 4, 4, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.3)"
            }
          },
          label: {
            show: true,
            position: "right",
            color: "#666",
            fontSize: 16,
            formatter: function(params) {
              const total = values.reduce((sum, val) => sum + val, 0);
              const percentage = ((params.value / total) * 100).toFixed(1);
              return `${params.value} (${percentage}%)`;
            }
          }
        }
      ]
    };
  };

  return (
    <>
      {/* 上方查詢區 */}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
            <div className="grid">
              <div className="col-12 lg:col-4">
                <label htmlFor="executionDate" className="form-label">
                  {t("action_date")}
                </label>
                <div className="custom-moth-picker">
                  <Button
                    type="button"
                    onClick={() => {
                      preMonth();
                    }}
                    icon="pi pi-angle-left"
                  />
                  <span style={{ display: currentDate === "" ? "none" : "" }}>
                    {mappingMath(currentDate.split("-")[1])}
                  </span>
                  <Button
                    type="button"
                    onClick={() => {
                      nextMonth();
                    }}
                    icon="pi pi-angle-right"
                  />
                </div>
              </div>
              <div className="col-12 lg:col-8 flex align-items-end">
                <div className="col-date-picker">
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("validation.required"),
                      },
                    }}
                    render={({ field, fieldState, formState }) => (
                      <Calendar
                        className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                        dateFormat="yy-mm-dd"
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e.value);
                          // if(e.value < watch("endDate")){
                          //     clearErrors("endDate")
                          // }
                        }}
                        showIcon
                      />
                    )}
                  />
                  {getFormErrorMessage(errors, "startDate")}

                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("validation.required"),
                      },
                    }}
                    render={({ field, fieldState, formState }) => (
                      <Calendar
                        className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                        dateFormat="yy-mm-dd"
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e.value);
                        }}
                        showIcon
                      />
                    )}
                  />
                  {getFormErrorMessage(errors, "endDate")}
                </div>
              </div>

              {/*關鍵字*/}
              {type === "2" && (
                <>
                  <div className="col-4">
                    <label htmlFor="effectiveType" className="form-label">
                      {t("authorizationType")}
                    </label>
                    <Controller
                      name="effectiveType"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Dropdown
                          filter
                          showClear
                          optionLabel="subitemName"
                          optionValue="subitemNo"
                          value={field.value}
                          virtualScrollerOptions={{ itemSize: 40 }}
                          defaultValue={""}
                          placeholder={t("plz_chs_all")}
                          name="effectiveType"
                          control={control}
                          options={effectiveType}
                          className={"w-full"}
                          onChange={(event) => {
                            field.onChange(event.value ?? null);
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="col-4">
                    <label htmlFor="tagSelect" className="form-label">
                      {t("tag")}
                    </label>
                    <Dropdown
                      filter
                      showClear
                      optionLabel="label"
                      optionValue="value"
                      value={selectedTag}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      placeholder={t("plz_chs_all")}
                      name="tagSelect"
                      options={tagList}
                      className={"w-full"}
                      onChange={(event) => {
                        setSelectedTag(event.value ?? null);
                      }}
                    />
                  </div>
                  <div className={"col-4"}>
                    <FormInput
                      register={register}
                      errors={errors}
                      id="keywordSearch"
                      type="text"
                      placeholder={`${t("companyName")}`}
                      labelClass="control-label"
                      enterClick={checkKeyDown}
                      labelText={t("keyword")}
                    />
                  </div>
                </>
              )}

              <div className="col-12 gap-2 flex-all-center">
                <Button disabled={!isValid} type="submit" label={t("button.query")} />
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 下方顯示區 */}
      <div className="p-card">
        <div className="p-card-body">
          <div className="grid align-items-center mb-3">
            {/* 左側：事件選項 */}
            <div className="col-12 lg:col-2">
              <Dropdown
                optionLabel="label"
                optionValue="value"
                value={type}
                virtualScrollerOptions={{ itemSize: 32 }}
                placeholder={t("plz_chs")}
                className={"w-full"}
                panelStyle={{ minWidth: "120px" }}
                options={[
                  { label: t("event"), value: "1" },
                  { label: t("corporation"), value: "2" },
                ]}
                onChange={(event) => {
                  changeType(event.value);
                }}
              />
            </div>

            {/* 中間空白區域 */}
            <div className="col-12 lg:col-6"></div>

            {/* 右側：檢視模式 */}
            <div className="col-12 lg:col-4 flex justify-content-end align-items-center">
              <div className="flex align-items-center gap-2">
                <span className="text-sm text-600">{t("viewMode")}:</span>
                <Button
                  icon="pi pi-table"
                  rounded
                  outlined={viewMode !== "table"}
                  severity={viewMode === "table" ? "primary" : "secondary"}
                  onClick={() => setViewMode("table")}
                />
                <Button
                  icon="pi pi-chart-bar"
                  rounded
                  outlined={viewMode !== "chart"}
                  severity={viewMode === "chart" ? "primary" : "secondary"}
                  onClick={() => setViewMode("chart")}
                />
                <Button
                  icon="pi pi-chart-pie"
                  rounded
                  outlined={viewMode !== "pie"}
                  severity={viewMode === "pie" ? "primary" : "secondary"}
                  onClick={() => setViewMode("pie")}
                />
              </div>
            </div>
          </div>

          {/* 圖表/表格顯示區域 */}
          {viewMode !== "table" && viewMode === "pie" && (
            <div className="grid">
              {/* 左側圓餅圖 */}
              <div className="col-12 lg:col-8">
                <div style={{ position: "relative", width: "100%", height: "600px" }}>
                  <ReactECharts
                    key={`${type}-${viewMode}-${showLegend ? "legend-on" : "legend-off"}`}
                    option={getPieOptions(columns)}
                    style={{ height: "100%", width: "100%" }}
                  />
                </div>
              </div>

              {/* 右側簡易表格 */}
              <div className="col-12 lg:col-4">
                <div
                  style={{ maxHeight: "600px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "4px" }}
                >
                  <table className="p-datatable-table" style={{ width: "100%", marginBottom: "0", fontSize: "16px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <th
                          style={{
                            padding: "0.5rem",
                            textAlign: "left",
                            borderBottom: "1px solid #dee2e6",
                            fontWeight: "600",
                          }}
                        >
                          {type === "1" ? t("eventName") : t("companyName")}
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            textAlign: "center",
                            borderBottom: "1px solid #dee2e6",
                            fontWeight: "600",
                          }}
                        >
                          {t("times")}
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            textAlign: "center",
                            borderBottom: "1px solid #dee2e6",
                            fontWeight: "600",
                          }}
                        >
                          {t("percentage")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const chartData = processChartData(columns);
                        return chartData.map((item, index) => (
                          <tr
                            key={index}
                            style={{ borderBottom: index < chartData.length - 1 ? "1px solid #f0f0f0" : "none" }}
                          >
                            <td style={{ padding: "0.4rem", textAlign: "left" }}>
                              {type === "1" ? item.eventName : item.companyName}
                            </td>
                            <td style={{ padding: "0.4rem", textAlign: "center" }}>{item.count}</td>
                            <td style={{ padding: "0.4rem", textAlign: "center" }}>{item.percentage}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 長條圖顯示區域 */}
          {viewMode === "chart" && (
            <div style={{ position: "relative", width: "100%", height: "600px" }}>
              <ReactECharts
                key={`${type}-${viewMode}-${showLegend ? "legend-on" : "legend-off"}`}
                option={getBarOptions(columns)}
                style={{ height: "100%", width: "100%" }}
              />
            </div>
          )}

          {viewMode === "table" && type === "1" && (
            <>
              {/* 一鍵展開按鈕 */}
              <div className="flex justify-content-end mb-2">
                <Button
                  label={isAllExpanded ? t("button.collapse") : t("button.expansion")}
                  icon={isAllExpanded ? "pi pi-angle-double-up" : "pi pi-angle-double-down"}
                  size="small"
                  severity="secondary"
                  outlined
                  onClick={toggleAllExpanded}
                />
              </div>
              <DataTable
                stripedRows
                className="white-data-table hover-highlight no-shadow"
                value={columns}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                  // 檢查是否所有行都已展開
                  const expandedCount = Object.keys(e.data).length;
                  const totalCount = columns.length;
                  setIsAllExpanded(expandedCount === totalCount && totalCount > 0);
                }}
                rowExpansionTemplate={rowExpansionTemplateByAction}
                dataKey="event"
                emptyMessage={<NoInfo panel="table" />}
              >
                <Column expander style={{ width: "3em" }} />
                <Column field="eventName" header={t("eventName")} />
                <Column field="count" header={t("times")} />
                <Column
                  field="percentage"
                  header={t("percentage")}
                  body={(rowData) => `${rowData.percentage}`}
                  style={{ width: "150px" }}
                />
              </DataTable>
            </>
          )}
          {viewMode === "table" && type === "2" && (
            <>
              {/* 一鍵展開按鈕 */}
              <div className="flex justify-content-end mb-2">
                <Button
                  label={isAllExpanded ? t("button.collapse") : t("button.expansion")}
                  icon={isAllExpanded ? "pi pi-angle-double-up" : "pi pi-angle-double-down"}
                  size="small"
                  severity="secondary"
                  outlined
                  onClick={toggleAllExpanded}
                />
              </div>
              <DataTable
                stripedRows
                className="white-data-table hover-highlight no-shadow"
                value={columns}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplateByCompany}
                dataKey="companySeq"
                emptyMessage={<NoInfo panel="table" />}
              >
                <Column expander style={{ width: "3em" }} />
                <Column
                  field="companyName"
                  header={t("companyName")}
                  style={{ maxWidth: "300px" }}
                  bodyStyle={{ textOverflow: "ellipsis" }}
                />
                <Column field="companyTypeName" header={t("type")} body={companyTypeNameTemplate} />
                <Column field="companyCountryName" header={t("country")} />
                <Column
                  field="effectiveTypeName"
                  header={t("authorizationType")}
                  body={companyLevelNameTemplate}
                  style={{ minWidth: "100px" }}
                />
                <Column field="count" header={t("times")} />
                <Column
                  field="percentage"
                  header={t("percentage")}
                  body={(rowData) => `${rowData.percentage}`}
                  style={{ width: "150px" }}
                />
                <Column
                  style={{ minWidth: "100px" }}
                  body={actionBodyTemplate}
                  header={t("action")}
                  frozen
                  alignFrozen="right"
                />
              </DataTable>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ActivityAnalyzeIndex;
